import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./dictionaries/en.json";
import uiEn from "./ui/en.json";
import { DEFAULT_LOCALE, ENABLED_CODES, getLocale } from "./locales";

/**
 * i18next setup for Statlign.
 *
 * - English is the canonical source and is bundled eagerly so first paint never
 *   waits on a network fetch (and English users pay zero i18n download cost
 *   beyond the tiny en.json already in the bundle).
 * - Every other enabled locale's dictionary is code-split and lazy-loaded on
 *   demand via a dynamic import, then cached by i18next.
 * - Keys are namespaced inside a single default namespace ("translation") using
 *   dotted paths (e.g. t("nav.groups.Predictions")).
 */

// Eagerly-bundled locale dictionaries (English only). Others load lazily.
const eagerResources: Record<string, { translation: Record<string, unknown>; ui: Record<string, string> }> = {
  en: { translation: en as Record<string, unknown>, ui: uiEn },
};

// Lazy loaders for every non-English enabled locale. Vite code-splits each
// JSON into its own chunk fetched only when that language is activated.
const lazyLoaders: Record<string, () => Promise<Record<string, unknown>>> = {
  es: () => import("./dictionaries/es.json").then((m) => m.default),
  pt: () => import("./dictionaries/pt.json").then((m) => m.default),
  fr: () => import("./dictionaries/fr.json").then((m) => m.default),
  de: () => import("./dictionaries/de.json").then((m) => m.default),
  it: () => import("./dictionaries/it.json").then((m) => m.default),
  ar: () => import("./dictionaries/ar.json").then((m) => m.default),
  ru: () => import("./dictionaries/ru.json").then((m) => m.default),
  ja: () => import("./dictionaries/ja.json").then((m) => m.default),
  ko: () => import("./dictionaries/ko.json").then((m) => m.default),
  hi: () => import("./dictionaries/hi.json").then((m) => m.default),
  tr: () => import("./dictionaries/tr.json").then((m) => m.default),
  id: () => import("./dictionaries/id.json").then((m) => m.default),
  pl: () => import("./dictionaries/pl.json").then((m) => m.default),
  nl: () => import("./dictionaries/nl.json").then((m) => m.default),
  el: () => import("./dictionaries/el.json").then((m) => m.default),
  sv: () => import("./dictionaries/sv.json").then((m) => m.default),
  fil: () => import("./dictionaries/fil.json").then((m) => m.default),
  am: () => import("./dictionaries/am.json").then((m) => m.default),
  vi: () => import("./dictionaries/vi.json").then((m) => m.default),
  ms: () => import("./dictionaries/ms.json").then((m) => m.default),
  zh: () => import("./dictionaries/zh.json").then((m) => m.default),
  "zh-TW": () => import("./dictionaries/zh-TW.json").then((m) => m.default),
};

const loaded = new Set<string>(["en"]);
const uiLoaders = import.meta.glob<{ default: Record<string, string> }>("./ui/*.json");

/**
 * Load (once) and register a locale's dictionary WITHOUT switching the global
 * language. Called from the root route's beforeLoad on server and client so
 * translated pages render their full content in the first HTML (crawlers).
 */
export async function ensureLocaleLoaded(code: string): Promise<void> {
  const target = ENABLED_CODES.includes(code) ? code : DEFAULT_LOCALE;
  if (loaded.has(target) || !lazyLoaders[target]) return;
  try {
    const [bundle, uiBundle] = await Promise.all([
      lazyLoaders[target](),
      uiLoaders[`./ui/${target}.json`]?.(),
    ]);
    i18n.addResourceBundle(target, "translation", bundle, true, true);
    if (uiBundle) {
      i18n.addResourceBundle(target, "ui", uiBundle.default, true, true);
      // Fill missing legacy keys only from genuinely translated source values.
      const reverse = new Map(Object.entries(uiEn).map(([key, value]) => [value, key]));
      const localize = (value: unknown): unknown => {
        if (typeof value === "string") {
          const key = reverse.get(value);
          return key && uiBundle.default[key] ? uiBundle.default[key] : undefined;
        }
        if (Array.isArray(value)) return value.map(localize);
        if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, localize(child)]).filter(([, child]) => child !== undefined));
        return value;
      };
      i18n.addResourceBundle(target, "translation", localize(en), true, false);
    }
    loaded.add(target);
  } catch (error) {
    console.error(`[i18n] Could not load ${target}`, error);
  }
}

/** Load (once) and register a locale's dictionary, then switch to it. */
export async function loadAndSetLocale(code: string): Promise<void> {
  const target = ENABLED_CODES.includes(code) ? code : DEFAULT_LOCALE;
  await ensureLocaleLoaded(target);
  if (i18n.language !== target) await i18n.changeLanguage(target);
}

/**
 * Per-locale i18next instance sharing the global resource store. On the server
 * the global instance is shared by concurrent requests, so each render gets
 * its own fixed-language clone instead of mutating the global language.
 */
const instances = new Map<string, typeof i18n>();
export function i18nFor(code: string): typeof i18n {
  const target = ENABLED_CODES.includes(code) ? code : DEFAULT_LOCALE;
  let inst = instances.get(target);
  if (!inst) {
    inst = i18n.cloneInstance({ lng: target, initAsync: false });
    instances.set(target, inst);
  }
  return inst;
}

const DEV = import.meta.env.DEV;

// Dev-only post-processor: detect fallback-to-English. i18next resolves a key
// against the fallback (en) silently, so `missingKeyHandler` never fires for
// "present in en, absent in the active locale". We catch that here by asking
// whether the active locale actually has the key (fallback disabled).
const trackFallback = {
  type: "postProcessor" as const,
  name: "trackFallback",
  process(value: string, key: string | string[], _opts: unknown, translator: { language?: string }) {
    const lng = translator?.language || i18n.language;
    if (lng && lng !== DEFAULT_LOCALE) {
      const k = Array.isArray(key) ? key[key.length - 1] : key;
      if (k && !i18n.exists(k, { lng, fallbackLng: false })) {
        void import("./missingKeys").then((m) =>
          m.recordMissingKey({ key: k, locale: lng, fallback: DEFAULT_LOCALE, rendered: value }),
        );
      }
    }
    return value;
  },
};

i18n.use(initReactI18next);
if (DEV) i18n.use(trackFallback);

i18n.init({
  resources: eagerResources,
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  defaultNS: "translation",
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  saveMissing: DEV,
  // Truly-missing keys (absent in the active locale AND in the English
  // fallback) — logged and collected for the dev debug panel.
  missingKeyHandler: DEV
    ? (lngs, _ns, key, fallbackValue) => {
        void import("./missingKeys").then((m) =>
          m.recordMissingKey({
            key,
            locale: Array.isArray(lngs) ? lngs[0] : String(lngs),
            fallback: "(none)",
            rendered: fallbackValue ?? "",
          }),
        );
      }
    : false,
  postProcess: DEV ? ["trackFallback"] : [],
  react: { useSuspense: false },
});

export { i18n, getLocale };
export default i18n;
